import UploadsAPI from 'api/UploadsAPI'

export default {
    data() {
        return {
            selectedFiles: [],
            isUploading: false,
            filesLimit: UploadsAPI.UPLOAD_MAX_SIZE,
            showWarning: false,
            warningText: null,
            isSubmitDisabled: false,
            showLimits: true,
            uploadScope: 'patient-documents',
        }
    },

    computed: {
        errorFilesCount() {
            return this.selectedFiles.filter((file) => file.errorMessage).length
        },

        uploadedFilesCount() {
            return this.selectedFiles.filter((file) => file.isUploaded).length
        },
    },

    methods: {
        onFileSelected(e) {
            Array.from(e.target.files).forEach((file) => {
                this.selectedFiles.push({
                    file: file,
                    fileSize: file.size,
                    progress: 0,
                    isUploaded: false,
                    uploadKey: null,
                    errorMessage: null,
                })
            })
            this.checkForFileSizes()
        },

        checkForFileSizes() {
            const oversize = this.selectedFiles.find((f) => f.file.size > this.filesLimit)
            if (oversize) {
                this.warningText = this.$t('pages.referrals.notes.add.warnings.size_limit')
                this.showWarning = true
                this.showLimits = true
                this.isSubmitDisabled = true
                return
            }
            // Also keep total-size guard for multi-file UX (optional soft limit: 10 × max)
            if (this.calculateFilesSize() > this.filesLimit * 10) {
                this.warningText = this.$t('pages.referrals.notes.add.warnings.size_limit')
                this.showWarning = true
                this.showLimits = true
                this.isSubmitDisabled = true
                return
            }
            if (this.selectedFiles.some((f) => f.errorMessage)) {
                this.isSubmitDisabled = true
                return
            }
            this.showWarning = false
            this.isSubmitDisabled = false
        },

        applyUploadError(error, pending = []) {
            const parsed = UploadsAPI.parseUploadError(error)
            const mimeError = parsed.code === 'upload.mime_type' || parsed.code === 'fileBase64.mimeType'
            const file = parsed.fileIndex != null ? pending[parsed.fileIndex] : null
            const perFileMessage = mimeError
                ? this.$t('common.upload.unsupported')
                : parsed.message || this.$t('pages.referrals.notes.add.warnings.error', { n: 1 })

            if (file) {
                this.$set(file, 'errorMessage', perFileMessage)
                file.progress = 0
            } else if (mimeError) {
                pending.forEach((f) => {
                    if (!f.isUploaded) this.$set(f, 'errorMessage', this.$t('common.upload.unsupported'))
                })
            }

            if (mimeError) {
                this.warningText = this.$t('pages.referrals.notes.add.warnings.mime_type')
            } else if (parsed.code === 'upload.max_size') {
                this.warningText = this.$t('pages.referrals.notes.add.warnings.size_limit')
            } else {
                this.warningText = this.$t('pages.referrals.notes.add.warnings.error', { n: 1 })
            }
            this.showWarning = true
            this.showLimits = true
            this.isSubmitDisabled = true
        },

        calculateFilesSize() {
            return this.selectedFiles.reduce((sum, file) => (sum += file.file.size), 0)
        },

        /** @deprecated Prefer prepareUploads(); kept for CSV/import callers that still need base64. */
        convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file.file)
                reader.onload = () =>
                    resolve({
                        ...file,
                        base64File: reader.result,
                    })
                reader.onerror = (error) => reject(error)
            })
        },

        /**
         * Presign + PUT each pending native File. Mutates selectedFiles with uploadKey + progress.
         * Already-uploaded entries (plain objects from a prior load) are skipped.
         * @returns {Promise<Array<{ uploadKey, fileName, size }>>}
         */
        async prepareUploads(scope) {
            const uploadScope = scope || this.uploadScope || 'patient-documents'
            const pending = this.selectedFiles.filter((f) => !f.isUploaded && f.file instanceof File)
            const nativeFiles = pending.map((f) => f.file)

            if (!nativeFiles.length) {
                return []
            }

            return UploadsAPI.uploadFiles(nativeFiles, uploadScope, (index, pct) => {
                if (pending[index]) {
                    pending[index].progress = pct
                }
            })
                .then((results) => {
                    results.forEach((result, index) => {
                        if (pending[index]) {
                            pending[index].uploadKey = result.uploadKey
                            pending[index].progress = 100
                        }
                    })
                    return results
                })
                .catch((error) => {
                    this.applyUploadError(error, pending)
                    throw error
                })
        },

        removeAndValidateFiles(file) {
            this.selectedFiles = this.selectedFiles.filter((f) => f !== file)
            this.checkForFileSizes()
        },
    },
}
